// src/components/ReportReviewCard.jsx
//
// "AI Actions to Review" — shared by the doctor home and the navigator
// dashboard. Each pending report shows what the AI found and what it suggests
// doing; the reviewer ticks the suggestions worth acting on (they become
// care-plan items) and either agrees with the AI or adjusts it with a note.
// A doctor and a navigator review the same report independently.
//
// Three tabs: "To review" (the queue), "Reviewed" (what this reviewer already
// decided, so an Agree doesn't just make the report vanish) and, for doctors
// only, "Recommendations" — the AI lifestyle points that reach the patient's
// Lifestyle tab once accepted. They are listed one page at a time and decided
// in bulk after a confirm step; the health record still accepts or rejects
// them one at a time.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { reportReviewAPI } from '../services/reportReviewApi';
import { doctorLifestyleItemAPI } from '../services/doctorApi';
import { useAuth } from '../context/AuthContext';
import Icon from './Icon';
import { matchesQuery } from '../utils/searchMatch';

const RISK_BADGE = { HIGH: 'badge-critical', CRITICAL: 'badge-critical', MODERATE: 'badge-moderate', LOW: 'badge-low' };
const URGENCY_COPY = { IMMEDIATE: 'Doctor attention: immediately', THIS_WEEK: 'Doctor attention: this week' };

const fmtDate = d => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

// `enabled: false` skips the fetch, for a caller that already holds the result
// and hands it to <ReportReviewCard reviews={…}>.
export function usePendingReviews(enabled = true) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await reportReviewAPI.pending();
      setItems(Array.isArray(res.data) ? res.data : []);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (enabled) load(); }, [load, enabled]);
  return { items, setItems, loading, failed, reload: load };
}

// Past reviews and (doctors only) the lifestyle-point queue. Failures here only
// blank their own tab; the main "To review" queue never depends on them.
function useReviewHistory() {
  const [reviewed, setReviewed] = useState([]);
  const load = useCallback(async () => {
    try {
      const res = await reportReviewAPI.reviewed();
      setReviewed(Array.isArray(res.data) ? res.data : []);
    } catch { /* leave the list as it was */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { reviewed, reloadReviewed: load };
}

const REC_PAGE_SIZE = 25;
const REC_BULK_MAX = 50;

// One page of the doctor's lifestyle-point queue, in the server's order. A
// server without paging (no pendingTotal in the reply) returns every pending
// point at once and ignores the params; the list is then treated as unpaged
// and searched in the browser.
function useLifestyleQueue(enabled) {
  const [page, setPage] = useState(0);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null); // { key, pending, reviewed, pendingTotal }
  const [legacy, setLegacy] = useState(false);
  const [failed, setFailed] = useState(false);
  const seq = useRef(0);
  const serverQ = legacy ? '' : q;
  const key = `${page}|${serverQ}`;

  const load = useCallback(async () => {
    if (!enabled) return;
    const mine = ++seq.current;
    setFailed(false);
    try {
      const res = await reportReviewAPI.lifestyle({ page, size: REC_PAGE_SIZE, ...(serverQ ? { q: serverQ } : {}) });
      if (mine !== seq.current) return;
      const d = res.data || {};
      const pending = Array.isArray(d.pending) ? d.pending : [];
      const paged = typeof d.pendingTotal === 'number';
      setLegacy(!paged);
      setData({
        key, pending,
        reviewed: Array.isArray(d.reviewed) ? d.reviewed : [],
        pendingTotal: paged ? d.pendingTotal : pending.length,
      });
    } catch {
      if (mine === seq.current) setFailed(true);
    }
  }, [enabled, page, serverQ, key]);

  useEffect(() => { load(); }, [load]);

  // Everything on the last page was decided: step back to the one before.
  useEffect(() => {
    if (data && data.key === key && data.pending.length === 0 && data.pendingTotal > 0 && page > 0) setPage(p => p - 1);
  }, [data, key, page]);

  const removeRows = useCallback(ids => {
    const gone = new Set(ids);
    setData(d => {
      if (!d) return d;
      const pending = d.pending.filter(p => !gone.has(p.id));
      return { ...d, pending, pendingTotal: Math.max(0, d.pendingTotal - (d.pending.length - pending.length)) };
    });
  }, []);

  return {
    page, setPage, q, setQ,
    pending: data?.pending || [],
    reviewed: data?.reviewed || [],
    pendingTotal: data?.pendingTotal || 0,
    paged: !legacy,
    loading: !failed && (!data || data.key !== key),
    failed,
    reload: load,
    removeRows,
  };
}

function ReviewedList({ items, onOpen }) {
  if (items.length === 0) {
    return <div className="card-row-meta">Reports you review will be listed here.</div>;
  }
  return (
    <div className="card-list">
      {items.map(r => {
        const level = (r.riskLevel || '').toUpperCase();
        return (
          <div key={r.reviewId}>
            <button type="button" className="card-row" onClick={() => onOpen(r.patientId)}>
              <div className="card-row-body">
                <div className="card-row-title">{r.patientName}</div>
                <div className="card-row-meta">
                  Reviewed {fmtDate(r.reviewedAt)} · drawn {fmtDate(r.drawDate)} ·{' '}
                  {r.acceptedActionCount
                    ? `${r.acceptedActionCount} action${r.acceptedActionCount === 1 ? '' : 's'} added to the care plan`
                    : 'no actions added'}
                </div>
              </div>
              <span className={`badge ${r.outcome === 'ADJUSTED' ? 'badge-moderate' : 'badge-low'}`}>
                {r.outcome === 'ADJUSTED' ? 'Adjusted' : 'Agreed'}
              </span>
              {level && (
                <span className={`badge ${RISK_BADGE[level] || 'badge-unknown'}`}>
                  {level}{r.riskScore != null ? ` · ${r.riskScore}` : ''}
                </span>
              )}
            </button>
            {r.note && <div className="card-row-meta">Your note: {r.note}</div>}
            {(r.actions || []).length > 0 && (
              <div className="card-list">
                {r.actions.map(a => (
                  <div key={a.id} className="card-row" style={{ alignItems: 'flex-start', gap: 10 }}>
                    <div className="card-row-body">
                      <div className="card-row-title">{a.title} {a.priority && <span className="badge badge-unknown">{a.priority}</span>}</div>
                      <div className="card-row-meta">{[a.detail, a.timeframe].filter(Boolean).join(' · ')}</div>
                    </div>
                    {a.approved != null && (
                      <span className={`badge ${a.approved ? 'badge-low' : 'badge-unknown'}`}>
                        {a.approved ? 'Approved' : 'Not added'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// A decided point in "Reviewed by you".
function PointRow({ point }) {
  return (
    <div className="card-row" style={{ alignItems: 'flex-start', gap: 10 }}>
      <div className="card-row-body">
        <div className="card-row-title">{point.habitChange}</div>
        <div className="card-row-meta">
          {[point.domain, point.timeWindow, point.drawDate && `Report ${fmtDate(point.drawDate)}`].filter(Boolean).join(' · ')}
        </div>
        {point.rationale && <div className="card-row-meta">{point.rationale}</div>}
      </div>
      <span className={`badge ${point.status === 'ACCEPTED' ? 'badge-low' : 'badge-unknown'}`}>
        {point.status === 'ACCEPTED' ? 'Accepted' : 'Rejected'}
      </span>
    </div>
  );
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const BULK_REASON = {
  NOT_FOUND: 'it no longer exists',
  NOT_YOUR_PATIENT: 'the patient is not under your care',
  ALREADY_REVIEWED: 'it was already reviewed',
  FAILED: "it couldn't be saved, please try again",
};

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Confirm step for a bulk accept or reject. Focus stays inside while it is
// open, Escape cancels, and focus goes back to whatever opened it. Portalled
// to <body> for the same reason as EscalationQueue's dialog: the card's
// fade-in-up transform would otherwise contain the fixed backdrop.
function ConfirmDialog({ title, sub, confirmLabel, busy, onConfirm, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const opener = document.activeElement;
    ref.current?.querySelector('button')?.focus();
    return () => { if (opener && opener.focus) opener.focus(); };
  }, []);

  function onKeyDown(ev) {
    if (ev.key === 'Escape') { ev.stopPropagation(); if (!busy) onClose(); return; }
    if (ev.key !== 'Tab') return;
    const nodes = [...ref.current.querySelectorAll(FOCUSABLE)];
    if (nodes.length === 0) { ev.preventDefault(); return; }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
    else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
  }

  return createPortal(
    <div className="dialog-backdrop" onClick={busy ? undefined : onClose}>
      <div ref={ref} className="dialog rrec-dialog" role="dialog" aria-modal="true"
        aria-labelledby="rrec-dialog-title" aria-describedby="rrec-dialog-sub"
        tabIndex={-1} onKeyDown={onKeyDown} onClick={ev => ev.stopPropagation()}>
        <div className="dialog-title" id="rrec-dialog-title">{title}</div>
        <div className="dialog-sub" id="rrec-dialog-sub">{sub}</div>
        <div className="dialog-actions">
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={onConfirm}>
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// The recommendation and its rationale; clicking the text shows them in full.
function RecText({ point }) {
  const [open, setOpen] = useState(false);
  return (
    <button type="button" className={`rrec-text${open ? ' rrec-text--open' : ''}`} aria-expanded={open}
      onClick={() => setOpen(o => !o)}>
      <span className="rrec-habit" title={point.habitChange}>{point.habitChange}</span>
      {point.rationale && <span className="rrec-why" title={point.rationale}>{point.rationale}</span>}
    </button>
  );
}

function RecommendationsTab({ queue }) {
  const [search, setSearch] = useState(queue.q);
  const [selected, setSelected] = useState(() => new Set());
  const [dialog, setDialog] = useState(null); // 'ACCEPT' | 'REJECT'
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { action, succeeded, failed: [{ id, reason, point }] }
  const [error, setError] = useState(null);   // { action, ids }
  const inFlight = useRef(false);
  const headerBox = useRef(null);
  const { setQ, setPage } = queue;

  // Send the search once typing pauses; a new search starts on the first page
  // with nothing selected.
  useEffect(() => {
    const next = search.trim();
    if (next === queue.q) return undefined;
    const t = setTimeout(() => { setQ(next); setPage(0); setSelected(new Set()); }, 400);
    return () => clearTimeout(t);
  }, [search, queue.q, setQ, setPage]);

  // An older server returns every row at once; search those in the browser.
  const rows = useMemo(() => {
    if (queue.paged || !queue.q) return queue.pending;
    const needle = queue.q.toLowerCase();
    return queue.pending.filter(p => [p.patientName, p.habitChange, p.domain, p.rationale, p.timeWindow]
      .some(v => v && String(v).toLowerCase().includes(needle)));
  }, [queue.pending, queue.paged, queue.q]);

  // Never keep a selection the user can no longer see.
  useEffect(() => {
    const ids = new Set(rows.map(p => p.id));
    setSelected(prev => {
      const next = new Set([...prev].filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const selectedRows = rows.filter(p => selected.has(p.id));
  const count = selectedRows.length;
  const allOn = rows.length > 0 && count === rows.length;
  useEffect(() => { if (headerBox.current) headerBox.current.indeterminate = count > 0 && !allOn; });

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function goTo(page) {
    setPage(page);
    setSelected(new Set());
  }

  const blocked = !queue.paged ? 'Bulk actions need the updated server'
    : busy ? 'Saving…'
    : count === 0 ? 'Select rows to accept or reject'
    : count > REC_BULK_MAX ? `Select ${REC_BULK_MAX} or fewer at a time`
    : '';

  async function runBulk(action, ids) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setError(null); setResult(null);
    const byId = new Map(queue.pending.map(p => [p.id, p]));
    try {
      const { data } = await doctorLifestyleItemAPI.bulk(ids, action);
      const succeeded = Array.isArray(data?.succeeded) ? data.succeeded : [];
      const failed = (Array.isArray(data?.failed) ? data.failed : []).map(f => ({ ...f, point: byId.get(f.id) }));
      setDialog(null);
      setSelected(new Set(failed.map(f => f.id)));
      setResult({ action, succeeded: succeeded.length, failed });
      queue.removeRows(succeeded);
      queue.reload();
    } catch {
      setDialog(null);
      setError({ action, ids });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  const patients = new Set(selectedRows.map(p => p.patientId)).size;
  const recs = plural(count, 'recommendation', 'recommendations');
  const pts = plural(patients, 'patient', 'patients');
  const they = count === 1 ? 'It' : 'They';
  const DIALOG = {
    ACCEPT: {
      title: `Accept ${recs} for ${pts}?`,
      sub: patients === 1
        ? `${they} will be added to the patient's Lifestyle tab and care plan.`
        : `${they} will be added to the patients' Lifestyle tabs and care plans.`,
      confirmLabel: `Accept ${count}`,
    },
    REJECT: {
      title: `Reject ${recs} for ${pts}?`,
      sub: patients === 1
        ? `${they} will not be added to the patient's Lifestyle tab or care plan.`
        : `${they} will not be added to the patients' Lifestyle tabs or care plans.`,
      confirmLabel: `Reject ${count}`,
    },
  };

  const verb = a => (a === 'ACCEPT' ? 'accepted' : 'rejected');
  const pageCount = queue.paged ? Math.max(1, Math.ceil(queue.pendingTotal / REC_PAGE_SIZE)) : 1;
  const from = queue.paged ? queue.page * REC_PAGE_SIZE + 1 : 1;
  const to = queue.paged ? queue.page * REC_PAGE_SIZE + rows.length : rows.length;
  const of = queue.paged ? queue.pendingTotal : rows.length;

  return (
    <div className="rrec">
      <div className="rrec-help">Accepted recommendations go to the patient's Lifestyle tab and care plan.</div>
      <div className="rrec-title" id="rrec-title">Recommendations ({queue.pendingTotal})</div>

      <div className="rrec-toolbar">
        <div className="search-input-wrap rrec-search">
          <label className="mlist-sr-only" htmlFor="rrec-search">Search recommendations</label>
          <span className="search-icon"><Icon name="search" size={15} /></span>
          <input id="rrec-search" className="search-input" type="search" maxLength={100}
            placeholder="Search recommendations" value={search}
            onChange={e => setSearch(e.target.value.slice(0, 100))} />
        </div>
        <div className="rrec-actions" role="group" aria-label="Bulk actions">
          <label className="rrec-check rrec-check--all">
            <input ref={headerBox} type="checkbox" checked={allOn} disabled={rows.length === 0}
              onChange={() => setSelected(allOn ? new Set() : new Set(rows.map(p => p.id)))} />
            <span className="mlist-sr-only">Select all on this page</span>
          </label>
          <span className="rrec-count" aria-live="polite">{count} selected</span>
          <span className="rrec-spacer" />
          {blocked && <span className="rrec-hint" id="rrec-hint">{blocked}</span>}
          <button type="button" className="btn btn-primary btn-sm rrec-btn" disabled={!!blocked}
            aria-describedby={blocked ? 'rrec-hint' : undefined}
            onClick={() => { setResult(null); setDialog('ACCEPT'); }}>
            Accept selected
          </button>
          <button type="button" className="btn btn-secondary btn-sm rrec-btn" disabled={!!blocked}
            aria-describedby={blocked ? 'rrec-hint' : undefined}
            onClick={() => { setResult(null); setDialog('REJECT'); }}>
            Reject selected
          </button>
        </div>
      </div>

      <div role="status">
        {result && (
          <div className={`alert ${result.failed.length ? 'alert-warning' : 'alert-success'} rrec-result`}>
            <div>
              {result.succeeded} {verb(result.action)}
              {result.failed.length > 0 && `, ${result.failed.length} failed`}
            </div>
            {result.failed.length > 0 && (
              <ul className="rrec-failures">
                {result.failed.map(f => (
                  <li key={f.id}>
                    {f.point ? `${f.point.habitChange} (${f.point.patientName})` : 'A recommendation'}:{' '}
                    {BULK_REASON[f.reason] || "it couldn't be saved"}.
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {error && (
        <div className="alert alert-error rrec-error" role="alert">
          <span>
            Couldn't {error.action === 'ACCEPT' ? 'accept' : 'reject'} the selected recommendations. Nothing was changed.
          </span>
          <button type="button" className="btn btn-ghost btn-sm rrec-btn" disabled={busy}
            onClick={() => runBulk(error.action, error.ids)}>
            Retry
          </button>
        </div>
      )}

      {queue.failed ? (
        <div className="alert alert-error rrec-error" role="alert">
          <span>Couldn't load recommendations.</span>
          <button type="button" className="btn btn-ghost btn-sm rrec-btn" onClick={queue.reload}>Retry</button>
        </div>
      ) : queue.loading ? (
        <div className="skeleton-list" aria-busy="true" aria-label="Loading recommendations">
          {[0, 1, 2].map(i => <div key={i} className="skeleton skeleton--row" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state rrec-empty">
          <div className="empty-title">{queue.q ? 'No recommendations match' : 'Nothing to review'}</div>
          <div className="empty-desc">
            {queue.q ? 'Try a different patient name or wording.' : 'No recommendations are waiting for review.'}
          </div>
        </div>
      ) : (
        <>
          <div className="rrec-table-wrap">
            <table className="rrec-table" aria-labelledby="rrec-title">
              <thead>
                <tr>
                  <th className="rrec-c-check"><span className="mlist-sr-only">Select</span></th>
                  <th className="rrec-c-patient">Patient</th>
                  <th className="rrec-c-text">Recommendation</th>
                  <th className="rrec-c-focus">Focus area</th>
                  <th className="rrec-c-window">Window</th>
                  <th className="rrec-c-date">Report date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(p => (
                  <tr key={p.id} className={`rrec-row${selected.has(p.id) ? ' rrec-row--selected' : ''}`}>
                    <td className="rrec-c-check">
                      <label className="rrec-check">
                        <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}
                          aria-label={`Select: ${p.habitChange}, ${p.patientName}`} />
                      </label>
                    </td>
                    <td className="rrec-c-patient">{p.patientName}</td>
                    <td className="rrec-c-text"><RecText point={p} /></td>
                    <td className="rrec-c-focus">{p.domain || '—'}</td>
                    <td className="rrec-c-window">{p.timeWindow || '—'}</td>
                    <td className="rrec-c-date">{fmtDate(p.drawDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rrec-footer">
            <span>Showing {from}–{to} of {of}</span>
            {pageCount > 1 && (
              <div className="rrec-pager">
                <button type="button" className="rrec-page-btn" aria-label="Previous page"
                  disabled={queue.page === 0} onClick={() => goTo(queue.page - 1)}>‹</button>
                <span>Page {queue.page + 1} of {pageCount}</span>
                <button type="button" className="rrec-page-btn" aria-label="Next page"
                  disabled={queue.page + 1 >= pageCount} onClick={() => goTo(queue.page + 1)}>›</button>
              </div>
            )}
          </div>
        </>
      )}

      {queue.reviewed.length > 0 && (
        <div className="rrec-reviewed">
          <div className="field-label" style={{ marginTop: 14 }}>Reviewed by you</div>
          <div className="card-list">
            {queue.reviewed.map(p => (
              <div key={p.id}>
                <div className="card-row-meta">{p.patientName} · {fmtDate(p.reviewedAt)}</div>
                <PointRow point={p} />
              </div>
            ))}
          </div>
        </div>
      )}

      {dialog && (
        <ConfirmDialog {...DIALOG[dialog]} busy={busy}
          onConfirm={() => runBulk(dialog, selectedRows.map(p => p.id))}
          onClose={() => setDialog(null)} />
      )}
    </div>
  );
}

function ReviewPanel({ item, onDone }) {
  const [accepted, setAccepted] = useState([]);
  const [adjusting, setAdjusting] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggle = id => setAccepted(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  async function submit(outcome) {
    if (outcome === 'ADJUSTED' && !note.trim()) { setError('Add a note saying what you would change.'); return; }
    setSaving(true); setError('');
    try {
      const res = await reportReviewAPI.submit(item.triageResultId, {
        outcome, note: outcome === 'ADJUSTED' ? note.trim() : null, acceptedActionIds: accepted,
      });
      onDone(item, res.data?.acceptedActionCount ?? accepted.length);
    } catch (e) {
      setError(e?.response?.status === 409
        ? 'This report was already reviewed by you.'
        : "Couldn't save the review — please try again.");
      setSaving(false);
    }
  }

  return (
    <div style={{ padding: '12px 4px 4px' }}>
      <div className="badge badge-unknown" style={{ marginBottom: 10 }}>
        <Icon name="bolt" size={12} /> AI-generated — review before acting
      </div>
      {item.hasAcuteAlert && (
        <div className="alert alert-error">{item.acuteAlertDetail || 'Acute alert on this report.'}</div>
      )}
      {item.highlightedMarkers?.length > 0 && (
        <div className="toolbar" style={{ marginBottom: 10 }}>
          <span className="field-label">Doctor flagged</span>
          {item.highlightedMarkers.map(m => (
            <span key={m.markerName} className="badge badge-moderate">{m.markerName}</span>
          ))}
        </div>
      )}
      {item.keyFindings && (<><div className="field-label">What the AI found</div><div className="field-text">{item.keyFindings}</div></>)}
      {item.recommendation && (<><div className="field-label" style={{ marginTop: 10 }}>AI recommendation</div><div className="field-text">{item.recommendation}</div></>)}
      {URGENCY_COPY[item.doctorUrgency] && <div className="card-row-meta" style={{ marginTop: 6 }}>{URGENCY_COPY[item.doctorUrgency]}</div>}

      <div className="field-label" style={{ marginTop: 14 }}>
        Suggested actions {item.suggestedActions?.length ? '— tick the ones to add to the care plan' : ''}
      </div>
      {(item.suggestedActions || []).length === 0 && <div className="field-text">The AI suggested no specific actions.</div>}
      {(item.suggestedActions || []).map(a => (
        <label key={a.id} className="card-row" style={{ cursor: 'pointer', alignItems: 'flex-start', gap: 10 }}>
          <input type="checkbox" checked={accepted.includes(a.id)} onChange={() => toggle(a.id)} style={{ marginTop: 3 }} />
          <div className="card-row-body">
            <div className="card-row-title">
              {a.title} <span className="badge badge-unknown">{a.priority}</span>
              {a.source === 'PATIENT_PLAN' && <span className="badge badge-confirmed" title="This is also on the action items the patient sees">On patient's plan</span>}
            </div>
            <div className="card-row-meta">{[a.detail, a.timeframe].filter(Boolean).join(' · ')}</div>
          </div>
        </label>
      ))}

      {adjusting && (
        <div style={{ marginTop: 10 }}>
          <label className="form-label" htmlFor={`adj-${item.triageResultId}`}>What would you change?</label>
          <textarea id={`adj-${item.triageResultId}`} className="form-textarea" value={note} maxLength={1000}
            onChange={e => setNote(e.target.value)} placeholder="e.g. Risk is lower than stated; repeat in 6 weeks instead." />
        </div>
      )}
      {error && <div className="alert alert-error" style={{ marginTop: 10 }}>{error}</div>}

      <div className="toolbar" style={{ marginTop: 12 }}>
        {adjusting ? (
          <>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => submit('ADJUSTED')}>Submit adjustment</button>
            <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => { setAdjusting(false); setError(''); }}>Cancel</button>
          </>
        ) : (
          <>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => submit('AGREED')}>
              Agree{accepted.length ? ` & add ${accepted.length} action${accepted.length === 1 ? '' : 's'}` : ''}
            </button>
            <button className="btn btn-secondary btn-sm" disabled={saving} onClick={() => setAdjusting(true)}>Adjust…</button>
          </>
        )}
      </div>
    </div>
  );
}

// Two ways to place it: the default is a card with its own title (doctor home);
// `bare` drops the card chrome and title, always renders, and takes `query` to
// filter by patient — for use as a tab body (navigator Escalations & Notes).
// `reviews` is an already-loaded usePendingReviews() result, so a parent that
// shows the count doesn't fetch twice.
export default function ReportReviewCard({ limit = 5, bare = false, query = '', reviews }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDoctor = user?.role === 'DOCTOR';
  const own = usePendingReviews(!reviews);
  const { items: allItems, setItems, loading, failed } = reviews || own;
  const items = useMemo(() => allItems.filter(i => matchesQuery(query, i.patientName)), [allItems, query]);
  const { reviewed: allReviewed, reloadReviewed } = useReviewHistory();
  const reviewed = useMemo(() => allReviewed.filter(r => matchesQuery(query, r.patientName)), [allReviewed, query]);
  const queue = useLifestyleQueue(isDoctor);
  const [tab, setTab] = useState('review'); // review | reviewed | recs
  const [openId, setOpenId] = useState(null);
  const [notice, setNotice] = useState('');
  const patientPath = id => (isDoctor ? `/doctor/patients/${id}` : `/patients/${id}`);

  function done(item, actionCount) {
    setItems(prev => prev.filter(i => i.triageResultId !== item.triageResultId));
    setOpenId(null);
    reloadReviewed();
    setNotice(`Review recorded for ${item.patientName}${actionCount ? ` · ${actionCount} action${actionCount === 1 ? '' : 's'} added to the care plan` : ''}. It is now under Reviewed.`);
  }

  const nothingToShow = !bare && !loading && !failed && items.length === 0 && reviewed.length === 0
    && queue.pendingTotal === 0 && !queue.q && queue.reviewed.length === 0 && !notice;
  if (nothingToShow) return null;

  const tabs = [
    { key: 'review', label: 'To review', count: items.length },
    { key: 'reviewed', label: 'Reviewed', count: null },
    ...(isDoctor ? [{ key: 'recs', label: 'Recommendations', count: queue.pendingTotal }] : []),
  ];

  return (
    <div className={bare ? undefined : 'card fade-in-up'} id="report-reviews" style={bare ? undefined : { marginBottom: 24 }}>
      {!bare && (
        <div className="card-title">
          AI Actions to Review {items.length > 0 && <span className="badge badge-moderate">{items.length}</span>}
        </div>
      )}
      <div className="tabs-bar" role="tablist">
        {tabs.map(t => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
            className={`tab-btn${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}{t.count ? ` (${t.count})` : ''}
          </button>
        ))}
      </div>
      {notice && <div className="alert alert-success">{notice}</div>}
      {failed && <div className="alert alert-error">Couldn't load reports to review.</div>}

      {tab === 'reviewed' && <ReviewedList items={reviewed} onOpen={id => navigate(patientPath(id))} />}

      {tab === 'recs' && isDoctor && (
        <RecommendationsTab queue={queue} />
      )}

      {tab === 'review' && (loading ? <div className="skeleton skeleton--row" /> : (
        <div className="card-list">
          {items.length === 0 && !failed && (
            <div className="card-row-meta">
              {query.trim() && allItems.length > 0 ? 'No reports match your search.' : 'Nothing waiting for review.'}
            </div>
          )}
          {items.slice(0, limit).map(item => {
            const level = (item.riskLevel || '').toUpperCase();
            const open = openId === item.triageResultId;
            return (
              <div key={item.triageResultId}>
                <button type="button" className="card-row" aria-expanded={open}
                  onClick={() => setOpenId(open ? null : item.triageResultId)}>
                  <div className="card-row-body">
                    <div className="card-row-title">{item.patientName}</div>
                    <div className="card-row-meta">
                      {item.labPartnerImport ? 'Lab partner report' : 'Report'} · drawn {fmtDate(item.drawDate)}
                      {item.hasAcuteAlert ? ' · acute alert' : ''}
                      {item.suggestedActions?.length ? ` · ${item.suggestedActions.length} suggested action${item.suggestedActions.length === 1 ? '' : 's'}` : ''}
                    </div>
                  </div>
                  <span className={`badge ${RISK_BADGE[level] || 'badge-unknown'}`}>
                    {level || 'UNKNOWN'}{item.riskScore != null ? ` · ${item.riskScore}` : ''}
                  </span>
                  <span aria-hidden="true">{open ? '▾' : '→'}</span>
                </button>
                {open && (
                  <>
                    <ReviewPanel item={item} onDone={done} />
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate(patientPath(item.patientId))}>
                      Open patient →
                    </button>
                  </>
                )}
              </div>
            );
          })}
          {items.length > limit && <div className="card-row-meta">+{items.length - limit} more waiting</div>}
        </div>
      ))}
    </div>
  );
}
